import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Calendar, RefreshCw, Copy, FileText, Video, Loader2, Crown, TrendingUp, BookOpen, Heart, Zap, MessageCircle, RotateCcw, Lock } from 'lucide-react';
import { copyToClipboard } from '../utils/clipboard';

const PERSIST_KEY = 'creatorflow_planner';

// Posts count per frequency
const FREQUENCY_POST_COUNT = { '1x': 4, '3x': 12, 'weekdays': 22, 'daily': 30 };

// Frequency display labels
const FREQUENCY_LABELS = {
  '1x': '1x/week (4)',
  '3x': '3x/week (12)',
  'weekdays': '5x/week (22)',
  'daily': 'Daily (30)'
};

const Planner = () => {
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [loadingPlanner, setLoadingPlanner] = useState(false);
  const [regeneratingDay, setRegeneratingDay] = useState(null);
  
  // Planner data state
  const [plannerData, setPlannerData] = useState(null);
  
  // View frequency - for switching visible posts
  const [viewFrequency, setViewFrequency] = useState('daily');

  // Form state - only for initial generation
  const [form, setForm] = useState({
    niche: '',
    platform: 'tiktok',
    frequency: 'daily',
    goal: 'growth',
    tone: 'educational'
  });

  // Check if user has Max plan
  const hasAccess = user?.subscriptionPlan === 'max';

  // Determine if planner is generated (locked state)
  const isGenerated = !!plannerData && plannerData.posts?.length > 0;

  // Get the max posts user can view based on what was generated
  const maxViewablePosts = plannerData?.totalPostsGenerated || 0;

  // Check if a frequency is allowed based on totalPostsGenerated
  const isFrequencyAllowed = useCallback((freq) => {
    if (!plannerData) return true;
    const requiredPosts = FREQUENCY_POST_COUNT[freq] || 30;
    return requiredPosts <= maxViewablePosts;
  }, [plannerData, maxViewablePosts]);

  // Get the list of allowed frequencies
  const getAllowedFrequencies = useCallback(() => {
    if (!plannerData) return Object.keys(FREQUENCY_POST_COUNT);
    return Object.keys(FREQUENCY_POST_COUNT).filter(freq => isFrequencyAllowed(freq));
  }, [plannerData, isFrequencyAllowed]);

  // Filter posts based on current view frequency
  const visiblePosts = useMemo(() => {
    if (!plannerData?.posts || plannerData.posts.length === 0) return [];
    const count = FREQUENCY_POST_COUNT[viewFrequency] || 30;
    // Only show up to the actually generated count
    const maxAvailable = Math.min(count, maxViewablePosts);
    return plannerData.posts.slice(0, maxAvailable).map((post, idx) => ({ ...post, day: idx + 1 }));
  }, [plannerData, viewFrequency, maxViewablePosts]);

  // Load planner from localStorage or URL param on mount
  useEffect(() => {
    const loadPlanner = async () => {
      const savedId = searchParams.get('savedId');
      
      if (savedId) {
        // Load from database by ID
        setLoadingPlanner(true);
        try {
          const res = await api.get(`/planner/${savedId}`);
          const data = res.data;
          
          // Handle both old format (posts at top level) and new format (content.days)
          const posts = data.content?.days || data.posts || [];
          const niche = data.content?.niche || data.niche;
          const platform = data.content?.platform || data.platform;
          const frequencyAtGeneration = data.content?.frequencyAtGeneration || data.frequencyAtGeneration;
          const totalPostsGenerated = data.content?.totalPostsGenerated || data.totalPostsGenerated || posts.length;
          
          setPlannerData({
            id: data.id,
            niche: niche,
            platform: platform,
            frequencyAtGeneration: frequencyAtGeneration,
            totalPostsGenerated: totalPostsGenerated,
            posts: posts,
            createdAt: data.createdAt
          });
          setViewFrequency(frequencyAtGeneration || 'daily');
          // Save to localStorage as well (normalized format)
          localStorage.setItem(PERSIST_KEY, JSON.stringify({
            id: data.id,
            niche,
            platform,
            frequencyAtGeneration,
            totalPostsGenerated,
            posts,
            createdAt: data.createdAt
          }));
        } catch (err) {
          console.error('[Planner] Failed to load planner:', err);
          toast.error('Failed to load planner');
          // Try loading from localStorage as fallback
          loadFromLocalStorage();
        } finally {
          setLoadingPlanner(false);
        }
      } else {
        // Load from localStorage
        loadFromLocalStorage();
      }
    };

    const loadFromLocalStorage = () => {
      try {
        const saved = localStorage.getItem(PERSIST_KEY);
        if (saved) {
          const data = JSON.parse(saved);
          // Handle both formats in localStorage too
          const posts = data.content?.days || data.posts || [];
          setPlannerData({
            ...data,
            posts: posts,
            totalPostsGenerated: data.content?.totalPostsGenerated || data.totalPostsGenerated || posts.length
          });
          setViewFrequency(data.frequencyAtGeneration || data.content?.frequencyAtGeneration || 'daily');
        }
      } catch (e) {
        console.error('Failed to load planner from localStorage');
      }
    };

    if (hasAccess) {
      loadPlanner();
    }
  }, [searchParams, api, hasAccess]);

  // Save planner to localStorage whenever it changes
  useEffect(() => {
    if (plannerData) {
      localStorage.setItem(PERSIST_KEY, JSON.stringify(plannerData));
    }
  }, [plannerData]);

  // Handle view frequency change
  const handleViewFrequencyChange = (newFrequency) => {
    if (!isFrequencyAllowed(newFrequency)) {
      toast.error(`You need to regenerate your planner to view ${FREQUENCY_LABELS[newFrequency]} posts. Your current plan has ${maxViewablePosts} posts.`);
      return;
    }
    setViewFrequency(newFrequency);
  };

  const angleConfig = {
    educational: { icon: BookOpen, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
    entertaining: { icon: Zap, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
    inspiring: { icon: Heart, color: 'text-pink-400', bg: 'bg-pink-500/10', border: 'border-pink-500/30' },
    viral: { icon: TrendingUp, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
    story: { icon: MessageCircle, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30' },
    controversial: { icon: Zap, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
    trending: { icon: TrendingUp, color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!form.niche) {
      toast.error('Please enter your niche');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/planner/generate', { 
        niche: form.niche, 
        platform: form.platform,
        frequency: form.frequency 
      });
      
      // Store the complete planner data with new structure
      const newPlannerData = {
        id: res.data.savedId,
        niche: res.data.niche,
        platform: res.data.platform,
        frequencyAtGeneration: res.data.frequencyAtGeneration,
        totalPostsGenerated: res.data.totalPostsGenerated,
        posts: res.data.days,
        createdAt: new Date().toISOString()
      };
      
      setPlannerData(newPlannerData);
      setViewFrequency(res.data.frequencyAtGeneration);
      
      const creditsUsed = res.data.creditsUsed || 0;
      toast.success(`${res.data.totalPostsGenerated}-post plan generated! (${creditsUsed} credits used)`);
      refreshUser();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Failed to generate plan');
    } finally {
      setLoading(false);
    }
  };

  const regenerateDay = useCallback(async (dayNum) => {
    if (!plannerData) return;
    
    setRegeneratingDay(dayNum);
    try {
      const res = await api.post('/planner/regenerate-day?day=' + dayNum, { 
        niche: plannerData.niche, 
        platform: plannerData.platform 
      });
      
      setPlannerData(prev => ({
        ...prev,
        posts: prev.posts.map((p, idx) => (idx + 1) === dayNum ? { ...res.data.day, day: dayNum } : p)
      }));
      toast.success('Day ' + dayNum + ' regenerated!');
    } catch (err) {
      toast.error('Failed to regenerate day');
    } finally {
      setRegeneratingDay(null);
    }
  }, [api, plannerData]);

  const copyDay = (day) => {
    const text = 'Day ' + day.day + ': ' + day.title + '\n\n' + day.description + '\n\nHook: ' + day.hookApproach + '\nAngle: ' + day.contentAngle;
    copyToClipboard(text);
    toast.success('Day copied to clipboard!');
  };

  const copyFullPlan = () => {
    const text = visiblePosts.map(day => 
      'Day ' + day.day + ': ' + day.title + '\n' + day.description + '\nHook: ' + day.hookApproach + '\nAngle: ' + day.contentAngle
    ).join('\n\n---\n\n');
    copyToClipboard(text);
    toast.success('Full plan copied!');
  };

  const createScript = (day) => {
    navigate('/scripts', { state: { topic: day.title } });
  };

  const openInEditor = (day) => {
    navigate('/editor', { state: { script: day.title + '\n\n' + day.description } });
  };

  // Start Over - delete from DB, clear localStorage, reset UI
  const handleStartOver = async () => {
    try {
      // Delete from database if we have an id
      if (plannerData?.id) {
        try {
          await api.delete(`/saved/${plannerData.id}`);
        } catch (err) {
          // Silent fail - planner data already cleared locally
        }
      }
      
      // Clear localStorage
      localStorage.removeItem(PERSIST_KEY);
      
      // Reset state
      setPlannerData(null);
      setViewFrequency('daily');
      setForm({
        niche: '',
        platform: 'tiktok',
        frequency: 'daily',
        goal: 'growth',
        tone: 'educational'
      });
      
      // Clear URL params
      navigate('/planner', { replace: true });
      
      toast.success('Planner reset. You can now create a new plan.');
    } catch (err) {
      toast.error('Failed to reset planner');
    }
  };

  // Show locked state for non-Max users
  if (!hasAccess) {
    return (
      <div className="space-y-6" data-testid="planner-locked">
        <div>
          <div className="flex items-center gap-4">
            <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              30-Day Content Planner
            </h1>
            <div className="flex items-center gap-2 px-4 py-2 bg-cyan-500 rounded-xl">
              <Crown className="w-5 h-5 text-white" />
              <span className="font-bold text-white">MAX</span>
            </div>
          </div>
          <p className="text-slate-400 mt-3 text-lg">Plan a month of content in one click</p>
        </div>

        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-cyan-500/20 to-violet-500/20 flex items-center justify-center">
                <Crown className="w-8 h-8 text-cyan-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Upgrade to Creator Max</h2>
              <p className="text-slate-400 max-w-md mx-auto">
                The 30-Day Content Planner is an exclusive feature for Creator Max subscribers. Plan an entire month of viral content with AI-generated ideas, hooks, and content angles.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto mb-8">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-700/40" data-testid="planner-benefit-posts">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Calendar className="w-4 h-4 text-cyan-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">30 posts instantly</p>
                  <p className="text-slate-500 text-xs">A full month of content, generated in seconds</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-700/40" data-testid="planner-benefit-hooks">
                <div className="w-8 h-8 rounded-lg bg-violet-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Zap className="w-4 h-4 text-violet-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Hook + angle included</p>
                  <p className="text-slate-500 text-xs">Every post comes with a scroll-stopping hook</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-700/40" data-testid="planner-benefit-frequency">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Posting frequency structure</p>
                  <p className="text-slate-500 text-xs">Choose daily, 5x, 3x, or weekly schedules</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-700/40" data-testid="planner-benefit-consistency">
                <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Crown className="w-4 h-4 text-amber-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Designed for viral consistency</p>
                  <p className="text-slate-500 text-xs">AI balances educational, entertaining & trending angles</p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Button 
                onClick={() => navigate('/billing')}
                className="bg-gradient-to-r from-cyan-500 to-violet-500 hover:opacity-90 text-white"
                data-testid="planner-upgrade-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to Creator Max - $49/month
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading state when fetching planner from DB
  if (loadingPlanner) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="planner">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-4">
            <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              30-Day Content Planner
            </h1>
            <div className="flex items-center gap-2 px-4 py-2 bg-cyan-500 rounded-xl">
              <Crown className="w-5 h-5 text-white" />
              <span className="font-bold text-white">MAX</span>
            </div>
          </div>
          <p className="text-slate-400 mt-3 text-lg">Plan a month of content in one click</p>
        </div>
        {isGenerated && (
          <Button 
            variant="outline" 
            onClick={copyFullPlan}
            className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
            data-testid="copy-full-plan"
          >
            <Copy className="w-4 h-4 mr-2" /> Copy Full Plan
          </Button>
        )}
      </div>

      {/* Tags and Controls - Show when planner exists */}
      {isGenerated && (
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/30">
              <Calendar className="w-3 h-3 mr-1" /> {visiblePosts.length} / {maxViewablePosts} posts shown
            </Badge>
            <Badge className="bg-slate-800 text-slate-300 border-slate-700">
              {plannerData.platform === 'tiktok' ? 'TikTok' : plannerData.platform === 'reels' ? 'Reels' : 'Shorts'}
            </Badge>
            <Badge className="bg-slate-800 text-slate-300 border-slate-700">
              {plannerData.niche}
            </Badge>
            <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/30">
              Generated: {FREQUENCY_LABELS[plannerData.frequencyAtGeneration]}
            </Badge>
          </div>
          <div className="flex items-center gap-4">
            {/* View frequency selector with lock indicators */}
            <Select 
              value={viewFrequency} 
              onValueChange={handleViewFrequencyChange}
            >
              <SelectTrigger className="w-48 bg-slate-900/50 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {Object.entries(FREQUENCY_LABELS).map(([key, label]) => {
                  const allowed = isFrequencyAllowed(key);
                  return (
                    <SelectItem 
                      key={key} 
                      value={key}
                      disabled={!allowed}
                      className={!allowed ? 'opacity-50' : ''}
                    >
                      <span className="flex items-center gap-2">
                        {!allowed && <Lock className="w-3 h-3" />}
                        {label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Button 
              variant="ghost" 
              onClick={handleStartOver} 
              className="text-slate-400 hover:text-white"
              data-testid="reset-plan-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Start Over
            </Button>
          </div>
        </div>
      )}

      {/* Frequency upgrade message */}
      {isGenerated && !isFrequencyAllowed(viewFrequency) && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-center gap-3">
          <Lock className="w-5 h-5 text-amber-400" />
          <p className="text-amber-300">
            To view more posts, click "Start Over" and regenerate with a higher frequency.
          </p>
        </div>
      )}

      {/* Form - Only show when NO planner exists */}
      {!isGenerated && (
        <Card className="bg-slate-800/30 border-slate-700/50">
          <CardContent className="p-6">
            <form onSubmit={handleGenerate} className="space-y-6">
              {/* Row 1: Niche and Platform */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-slate-300">Niche / Topic</Label>
                  <Input
                    data-testid="planner-niche"
                    placeholder="fitness"
                    value={form.niche}
                    onChange={(e) => setForm({...form, niche: e.target.value})}
                    className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Platform</Label>
                  <Select 
                    value={form.platform} 
                    onValueChange={(v) => setForm({...form, platform: v})}
                  >
                    <SelectTrigger data-testid="planner-platform" className="bg-slate-900/50 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="tiktok">TikTok</SelectItem>
                      <SelectItem value="reels">Instagram Reels</SelectItem>
                      <SelectItem value="shorts">YouTube Shorts</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 2: Frequency, Goal, Tone */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label className="text-slate-300">Posting Frequency</Label>
                  <Select 
                    value={form.frequency} 
                    onValueChange={(v) => setForm({...form, frequency: v})}
                  >
                    <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="daily">Daily (30 posts) - 25 credits</SelectItem>
                      <SelectItem value="weekdays">5x/week (22 posts) - 20 credits</SelectItem>
                      <SelectItem value="3x">3x/week (12 posts) - 10 credits</SelectItem>
                      <SelectItem value="1x">1x/week (4 posts) - 5 credits</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Content Goal</Label>
                  <Select 
                    value={form.goal} 
                    onValueChange={(v) => setForm({...form, goal: v})}
                  >
                    <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="growth">Growth & Reach</SelectItem>
                      <SelectItem value="engagement">Engagement</SelectItem>
                      <SelectItem value="authority">Authority Building</SelectItem>
                      <SelectItem value="viral">Viral Content</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Tone</Label>
                  <Select 
                    value={form.tone} 
                    onValueChange={(v) => setForm({...form, tone: v})}
                  >
                    <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="educational">Educational</SelectItem>
                      <SelectItem value="entertaining">Entertaining</SelectItem>
                      <SelectItem value="inspiring">Inspiring</SelectItem>
                      <SelectItem value="casual">Casual & Relatable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Generate Button */}
              <Button 
                type="submit" 
                className="w-full h-14 bg-gradient-to-r from-cyan-500 to-violet-500 hover:opacity-90 text-white text-lg font-medium"
                data-testid="generate-plan-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating Plan...
                  </>
                ) : (
                  <>
                    <Calendar className="w-5 h-5 mr-2" />
                    Generate {FREQUENCY_POST_COUNT[form.frequency]}-Post Plan
                  </>
                )}
              </Button>
              <p className="text-center text-slate-500 text-sm">
                Uses {form.frequency === '1x' ? 5 : form.frequency === '3x' ? 10 : form.frequency === 'weekdays' ? 20 : 25} credits
              </p>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Plan Grid - Show generated content */}
      {isGenerated && visiblePosts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visiblePosts.map((day) => {
            const angleData = angleConfig[day.contentAngle] || angleConfig.educational;
            const AngleIcon = angleData.icon;
            
            return (
              <Card 
                key={day.day} 
                className="bg-slate-800/30 border-slate-700/50 hover:border-slate-600/50 transition-colors"
                data-testid="planner-day-card"
              >
                <CardContent className="p-5">
                  {/* Header: Day badge + actions */}
                  <div className="flex items-center justify-between mb-4">
                    <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/30 font-semibold">
                      Day {day.day}
                    </Badge>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => regenerateDay(day.day)}
                        disabled={regeneratingDay === day.day}
                        className="h-8 w-8 text-slate-400 hover:text-white"
                        data-testid={'regenerate-day-' + day.day}
                      >
                        {regeneratingDay === day.day ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyDay(day)}
                        className="h-8 w-8 text-slate-400 hover:text-white"
                        data-testid={'copy-day-' + day.day}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="font-bold text-white text-lg mb-2 line-clamp-2">{day.title}</h3>
                  
                  {/* Description */}
                  <p className="text-slate-400 text-sm mb-4 line-clamp-3">{day.description}</p>
                  
                  {/* Hook Approach */}
                  <div className="mb-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Hook Approach</p>
                    <p className="text-slate-300 text-sm line-clamp-2">{day.hookApproach}</p>
                  </div>

                  {/* Content Angle Badge */}
                  <div className="mb-4">
                    <Badge className={`${angleData.bg} ${angleData.color} ${angleData.border}`}>
                      <AngleIcon className="w-3 h-3 mr-1" />
                      {day.contentAngle}
                    </Badge>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => createScript(day)}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                      data-testid={'script-day-' + day.day}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Script
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openInEditor(day)}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                      data-testid={'editor-day-' + day.day}
                    >
                      <Video className="w-4 h-4 mr-2" />
                      Editor
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Planner;
